import { useEffect, useRef, useState } from 'react';
import { confirmDialog } from './ConfirmDialog';
import { NumberStepper } from './NumberStepper';
import { SAVED_CONFIRMATION_MS, SaveButton, SaveRow, useSaveConfirmation } from './SaveFeedback';
import { LOGIN_LIMIT_HELP_URL } from '../config/wizard-config';
import { RichText } from '../i18n/RichText';
import { fieldDir } from '../i18n/field-dir';
import { readDevWizardPreview, type WizardPreview } from '../install-route';
import { copyText } from '../services/clipboard';
import { getCurrentLocale } from '../services/language-manager';
import { admitUrlProblem } from '../services/login-limit-admit-url';
import {
  askOn,
  exceptionsPayload,
  loginIdLabelShown,
  loginIdLabelToSave,
  zoneNeeded,
} from '../services/login-limit-login-id';
import { loginLimitReportWait, reportKey } from '../services/login-limit-report-wait';
import { t } from '../services/language-manager';
import { registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import {
  GenesysUnavailableError,
  isSessionExpired,
  SessionExpiredError,
  useSessionExpired,
} from '../services/session';
import {
  connectLoginLimit,
  curatedLicenseTypeName,
  disconnectLoginLimit,
  getLoginLimit,
  isLicenseTypeId,
  isRetiredLicenseTypeId,
  saveNeedsConnection,
  MAX_LICENSE_TYPE_ROWS,
  saveLoginLimit,
  setLoginLimitPrivateLink,
  verifyLoginLimit,
  type LicenseTypeLimitAction,
  type LicenseTypeOptions,
  type LoginLimitVerifyCode,
  LoginLimitApiError,
  type LoginLimitAdmitKind,
  type LoginLimitMode,
  type LoginLimitPeriodUnit,
  type LoginLimitSaveBody,
  type LoginLimitSettings,
  type LoginLimitStatus,
  type LoginLimitType,
} from '../services/login-limit-api';
import {
  PERIOD_DAYS,
  PERIOD_MONTHS,
  clampPeriodText,
  currentBillingPeriod,
  formatLocalTime,
  periodDayLabels,
  periodTextCanBecomeValid,
  type BillingPeriod,
} from '../services/login-limit-period';

/** The gate gives up on a billing report after 4 minutes (`BILLABLE_DEADLINE_MS`), so polling stops then too. */
const REPORT_POLL_MS = 4 * 60 * 1000;
const REPORT_POLL_INTERVAL_MS = 10_000;
/** After the 4-minute wait, while the report is still missing, so a late model still fills in. */
const REPORT_SLOW_POLL_MS = 60_000;

interface LoginLimitSectionProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** No top border when this is the only section on the page. */
  first?: boolean;
  /**
   * False in a Login Limit only org, whose Settings has no Guard block to set the
   * threat alert mailbox, so the alert hint does not point there.
   */
  threatMailbox?: boolean;
  /** Dev preview: sample data, no API calls. */
  preview?: boolean;
}

interface FormState {
  mode: LoginLimitMode;
  type: LoginLimitType | '';
  limit: string;
  periodStart: string;
  periodUnit: LoginLimitPeriodUnit;
  periodLength: string;
  /** One month is the stored default. Several months and days keep their own number. */
  periodChoice: 'one' | 'months' | 'days';
  admitKind: LoginLimitAdmitKind;
  admitUrl: string;
  customMessage: string;
  allowlist: string;
  alertEnabled: boolean;
  alertEmail: string;
  /**
   * Every type but named. Named always asks, so the switch is locked on.
   * Not written: the organizations-row contract has no attribute for it yet.
   */
  askForLoginId: boolean;
  /** Empty means the login page shows "Work email" in the user's language. Not written yet. */
  loginIdLabel: string;
  licenseTypes: LicenseTypeRow[];
  /** True once the organization row has stored the list, including an empty one. */
  licenseTypesKnown: boolean;
  /** Named switch. On when the organization has never stored it. */
  exceptionsReport: boolean;
  reportTimeZone: string;
  /** True once either report attribute has been stored, so a later mode can clear it. */
  exceptionsStored: boolean;
}

interface LicenseTypeRow {
  id: string;
  limit: string;
  action: LicenseTypeLimitAction;
}

const EMPTY_FORM: FormState = {
  mode: 'off',
  type: '',
  limit: '',
  periodStart: '',
  periodUnit: 'month',
  periodLength: '1',
  periodChoice: 'one',
  admitKind: 'workspace',
  admitUrl: '',
  customMessage: '',
  allowlist: '',
  alertEnabled: true,
  alertEmail: '',
  askForLoginId: false,
  loginIdLabel: '',
  licenseTypes: [],
  licenseTypesKnown: false,
  exceptionsReport: true,
  reportTimeZone: '',
  exceptionsStored: false,
};

const PREVIEW_STATUS: LoginLimitStatus = {
  connection: 'connected',
  slug: 'acme',
  loginUrl: 'https://login.mediabriz.com/acme',
  genesysName: 'Acme',
  report: {
    state: 'complete',
    model: 'concurrent',
    tier: 3,
    digital: false,
    updatedAt: '2026-10-25T13:41:00Z',
    hourlyLoggedInWarning: false,
    mixed: false,
    delayed: false,
    retryAt: null,
  },
  genesysUnavailable: false,
  genesysLastFailureAt: null,
  hourlyInteractingExcluded: 0,
  license: { readable: true, updatedAt: '2026-10-25T13:41:00Z', countingAllUsers: false, hourlyLicenseIdExposed: true },
  definitionTier: 3,
  sample: { count: 4, at: '2026-10-25T13:41:00Z', stale: false },
  named: null,
  privateLink: false,
  nudge: null,
  copy: null,
};

const PREVIEW_CHECKING_STATUS: LoginLimitStatus = {
  ...PREVIEW_STATUS,
  report: { ...PREVIEW_STATUS.report!, state: 'in_progress', model: null, tier: null, updatedAt: null },
  definitionTier: null,
};

/** A report the gate gave up on for now, to try again in an hour. */
function previewDelayedStatus(): LoginLimitStatus {
  return {
    ...PREVIEW_STATUS,
    report: {
      ...PREVIEW_STATUS.report!,
      state: 'unavailable',
      model: null,
      tier: null,
      updatedAt: null,
      delayed: true,
      retryAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    },
    definitionTier: null,
  };
}

type PreviewScenario = Pick<WizardPreview, 'loginLimitState' | 'loginLimitReport'>;

interface PreviewStart {
  status: LoginLimitStatus;
  form: FormState;
  savedMode: LoginLimitMode;
  savedType: LoginLimitType | null;
}

/** Dev preview only: `?llState=` and `?llReport=` pick the screen to check. */
function previewScenario(): PreviewScenario {
  const parsed = readDevWizardPreview();
  return {
    loginLimitState: parsed?.loginLimitState ?? 'connected',
    loginLimitReport: parsed?.loginLimitReport ?? 'concurrent',
  };
}

function previewStart(state: PreviewScenario['loginLimitState']): PreviewStart {
  const saved: FormState = { ...EMPTY_FORM, mode: 'monitor', type: 'concurrent', limit: '200' };
  if (state === 'not-connected' || state === 'invalid-client') {
    return {
      status: {
        ...PREVIEW_STATUS,
        connection: state === 'invalid-client' ? 'invalid_client' : 'disconnected',
        report: null,
        hourlyInteractingExcluded: null,
      },
      form: { ...saved, mode: 'block' },
      savedMode: 'block',
      savedType: 'concurrent',
    };
  }
  if (state === 'checking') return { status: PREVIEW_CHECKING_STATUS, form: EMPTY_FORM, savedMode: 'off', savedType: null };
  if (state === 'delayed') return { status: previewDelayedStatus(), form: EMPTY_FORM, savedMode: 'off', savedType: null };
  if (state === 'mismatch') {
    return { status: PREVIEW_STATUS, form: { ...saved, type: 'named', periodStart: '2026-10-01' }, savedMode: 'monitor', savedType: 'named' };
  }
  return { status: PREVIEW_STATUS, form: saved, savedMode: 'monitor', savedType: 'concurrent' };
}

/**
 * The billing report a preview check ends with. `slow` never finishes; `late` names concurrent
 * only once 5 minutes have passed since the check started.
 */
function previewReportDone(
  status: LoginLimitStatus,
  outcome: PreviewScenario['loginLimitReport'],
  checkStartedAt: number,
): LoginLimitStatus {
  if (outcome === 'slow' || status.report?.state !== 'in_progress') return status;
  if (outcome === 'late' && Date.now() - checkStartedAt < 5 * 60 * 1000) return status;
  const report = PREVIEW_STATUS.report!;
  if (outcome === 'concurrent' || outcome === 'named') return { ...PREVIEW_STATUS, report: { ...report, model: outcome } };
  if (outcome === 'late') return { ...PREVIEW_STATUS, report: { ...report, model: 'concurrent' } };
  if (outcome === 'none') return { ...PREVIEW_STATUS, report: { ...report, model: null, tier: null }, definitionTier: null };
  if (outcome === 'mixed') return { ...PREVIEW_STATUS, report: { ...report, model: null, tier: null, mixed: true } };
  return previewDelayedStatus();
}

const PREVIEW_LICENSE_TYPES: LicenseTypeOptions = {
  state: 'ready',
  options: [
    { id: 'agentCopilotLicense', name: 'Genesys Agent Copilot', counting: 'online' },
    { id: 'communicate', name: 'Communicate', counting: 'online' },
    { id: 'digitalAddOn', name: 'Digital add-on', counting: 'online' },
  ],
};

const PRIVATE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const LOGIN_ORIGINS = ['https://login.mediabriz.com', 'https://login-test.mediabriz.com'];

/** The sign-in host the gate's link uses: login-test on the Test gate, else login.mediabriz.com. */
function loginOrigin(url: string | null): string {
  let origin = '';
  try {
    origin = url ? new URL(url).origin : '';
  } catch {
    origin = '';
  }
  return LOGIN_ORIGINS.includes(origin) ? origin : LOGIN_ORIGINS[0];
}

function previewPrivateKey(): string {
  let key = '';
  for (let i = 0; i < 12; i += 1) {
    key += PRIVATE_ALPHABET[Math.floor(Math.random() * PRIVATE_ALPHABET.length)];
  }
  return key;
}

const EMAIL_RE = /^[^\s@<>"'`]+@[^\s@<>"'`]+\.[^\s@<>"'`.]+$/;
const MARKUP_RE = /<\s*\/?\s*[a-z!]|javascript\s*:|vbscript\s*:/i;

function formFromSettings(settings: LoginLimitSettings): FormState {
  return {
    mode: settings.loginLimitMode,
    type: settings.loginLimitType ?? '',
    limit: settings.loginLimitLimit === null ? '' : String(settings.loginLimitLimit),
    periodStart: settings.loginLimitPeriodStart ?? '',
    periodUnit: settings.loginLimitPeriodUnit ?? 'month',
    periodLength: settings.loginLimitPeriodLength === null ? '1' : String(settings.loginLimitPeriodLength),
    periodChoice: settings.loginLimitPeriodUnit === 'day'
      ? 'days'
      : settings.loginLimitPeriodUnit === 'month'
        && settings.loginLimitPeriodLength !== null
        && settings.loginLimitPeriodLength !== 1
        ? 'months'
        : 'one',
    admitKind: settings.loginLimitAdmitKind,
    admitUrl: settings.loginLimitAdmitUrl ?? '',
    customMessage: settings.loginLimitCustomMessage,
    allowlist: settings.loginLimitAllowlist.join('\n'),
    alertEnabled: settings.loginLimitAlertEnabled,
    alertEmail: settings.loginLimitAlertEmail,
    askForLoginId: settings.loginLimitAskForLoginId,
    loginIdLabel: settings.loginLimitLoginIdLabel,
    licenseTypes: (settings.loginLimitLicenseTypes ?? []).map((row) => ({
      id: row.id,
      limit: String(row.limit),
      action: row.action,
    })),
    licenseTypesKnown: settings.loginLimitLicenseTypes !== null,
    exceptionsReport: settings.loginLimitExceptionsReport !== false,
    reportTimeZone: settings.loginLimitReportTimeZone ?? '',
    exceptionsStored: settings.loginLimitExceptionsReport !== null || settings.loginLimitReportTimeZone !== null,
  };
}

/**
 * The name the gate sends, else the curated name, else the id. The name is never translated
 * in any language: admins match it against their Genesys bill. Communicate is the only type
 * held without a CX 1-4 seat, so it carries a translated "standalone" qualifier.
 */
function licenseTypeLabel(id: string, options: LicenseTypeOptions): string {
  const name = options.options.find((option) => option.id === id)?.name ?? curatedLicenseTypeName(id) ?? id;
  return id === 'communicate' ? t('login-limit-license-types-standalone', { name }) : name;
}

/** A saved row the gate no longer lists. The server accepts it only unchanged. */
function licenseTypeWithdrawn(id: string, options: LicenseTypeOptions): boolean {
  return isRetiredLicenseTypeId(id) || (options.state === 'ready' && !options.options.some((option) => option.id === id));
}

function licenseTypesProblem(rows: LicenseTypeRow[]): boolean {
  if (rows.length > MAX_LICENSE_TYPE_ROWS) return true;
  const seen = new Set<string>();
  for (const row of rows) {
    if (!isLicenseTypeId(row.id) || seen.has(row.id)) return true;
    seen.add(row.id);
    if (row.action !== 'alert' && row.action !== 'block') return true;
    if (limitNumber(row.limit) === null) return true;
  }
  return false;
}

const intlWithZones = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
const REPORT_ZONES: readonly string[] = typeof intlWithZones.supportedValuesOf === 'function'
  ? intlWithZones.supportedValuesOf('timeZone')
  : [];

function timeZoneOk(value: string): boolean {
  if (!value || value.length > 100) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function browserTimeZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zone && timeZoneOk(zone) ? zone : 'UTC';
}

const LOGIN_ID_LABEL_MAX = 40;

function clipLoginIdLabel(value: string): string {
  return [...value].slice(0, LOGIN_ID_LABEL_MAX).join('');
}

/** Digits only. Letters and symbols are dropped. An empty field stays empty. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function limitNumber(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value.trim());
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

function usesPeriod(type: FormState['type']): boolean {
  return type === 'named' || type === 'hours';
}

function periodLength(form: FormState): number | null {
  if (form.periodChoice === 'one') return 1;
  const parsed = limitNumber(form.periodLength);
  if (parsed === null) return null;
  if (form.periodChoice === 'days') return parsed >= PERIOD_DAYS.min && parsed <= PERIOD_DAYS.max ? parsed : null;
  return parsed >= PERIOD_MONTHS.min && parsed <= PERIOD_MONTHS.max ? parsed : null;
}

/** One and two days have their own sentences: Hebrew uses a dual form for two. */
function periodPreviewText(period: BillingPeriod): string {
  const days = periodDayLabels(period, getCurrentLocale());
  if (period.upcoming) return t('login-limit-period-preview-upcoming', days);
  if (period.resetsInDays === 1) return t('login-limit-period-preview-tomorrow', days);
  if (period.resetsInDays === 2) return t('login-limit-period-preview-two', days);
  return t('login-limit-period-preview', { ...days, days: String(period.resetsInDays) });
}

function hasMarkup(value: string): boolean {
  return MARKUP_RE.test(value);
}

function messageProblem(value: string): boolean {
  const text = value.replace(/\r\n/g, '\n');
  return hasMarkup(text) || [...text].length > 500 || (text.match(/\n/g)?.length ?? 0) > 10;
}

function labelProblem(value: string): boolean {
  return hasMarkup(value) || [...value].length > LOGIN_ID_LABEL_MAX;
}

/** The Login ID label is checked only while it is shown. */
function shownLabelProblem(form: FormState): boolean {
  return loginIdLabelShown(form) && labelProblem(form.loginIdLabel);
}

function saveBody(form: FormState, savedLoginIdLabel: string): LoginLimitSaveBody {
  const periodOk = usesPeriod(form.type) && form.periodStart !== '' && periodLength(form) !== null;
  return {
    loginLimitMode: form.mode,
    loginLimitType: form.type || null,
    loginLimitLimit: limitNumber(form.limit),
    loginLimitPeriodStart: periodOk ? form.periodStart : null,
    loginLimitPeriodUnit: periodOk ? (form.periodChoice === 'days' ? 'day' : 'month') : null,
    loginLimitPeriodLength: periodOk ? periodLength(form) : null,
    loginLimitAdmitKind: form.admitKind,
    loginLimitAdmitUrl: form.admitKind === 'custom' ? form.admitUrl.trim() : null,
    loginLimitCustomMessage: form.customMessage,
    loginLimitAllowlist: form.allowlist.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
    loginLimitAlertEmail: form.alertEmail.trim(),
    loginLimitAlertEnabled: form.alertEnabled,
    loginLimitAskForLoginId: askOn(form),
    loginLimitLoginIdLabel: loginIdLabelToSave(form, savedLoginIdLabel, !labelProblem(form.loginIdLabel)),
    ...licenseTypesPayload(form),
    ...exceptionsPayload(form),
  };
}

function licenseTypesPayload(form: FormState): Pick<LoginLimitSaveBody, 'loginLimitLicenseTypes'> | Record<string, never> {
  if (!form.licenseTypesKnown && form.licenseTypes.length === 0) return {};
  return {
    loginLimitLicenseTypes: form.licenseTypes.flatMap((row) => {
      const limit = limitNumber(row.limit);
      if (limit === null) return [];
      return [{ id: row.id, limit, action: row.action }];
    }),
  };
}

/**
 * Monitor or Block needs a main limit, or at least one license type limit. A main limit needs
 * its type, and the hours budget is its own limit.
 */
function limitProblemKey(form: FormState): string | null {
  if (form.limit.trim() && limitNumber(form.limit) === null) return 'login-limit-limit-invalid';
  if (limitNumber(form.limit) !== null) return null;
  if (form.type === 'hours') return 'login-limit-limit-invalid';
  return form.licenseTypes.some((row) => limitNumber(row.limit) !== null) ? null : 'login-limit-limit-required';
}

function problemKey(form: FormState, needsConnect: boolean): string | null {
  if (form.mode === 'off') {
    if (form.admitKind === 'custom' && form.admitUrl.trim()) {
      const urlProblem = admitUrlProblem(form.admitUrl);
      if (urlProblem) return urlProblem;
    }
    if (messageProblem(form.customMessage)) return 'login-limit-message-invalid';
    if (form.alertEmail.trim() && !EMAIL_RE.test(form.alertEmail.trim())) return 'login-limit-alert-invalid';
    if (shownLabelProblem(form)) return 'login-limit-login-id-invalid';
    if (licenseTypesProblem(form.licenseTypes)) return 'login-limit-license-types-invalid';
    if (zoneNeeded(form) && !timeZoneOk(form.reportTimeZone)) return 'login-limit-time-zone-required';
    return null;
  }
  if (licenseTypesProblem(form.licenseTypes)) return 'login-limit-license-types-invalid';
  if (zoneNeeded(form) && !timeZoneOk(form.reportTimeZone)) return 'login-limit-time-zone-required';
  const limitKey = limitProblemKey(form);
  if (limitKey) return limitKey;
  if (limitNumber(form.limit) !== null && !form.type) return 'login-limit-type-required';
  if (usesPeriod(form.type)) {
    if (!form.periodStart) return 'login-limit-period-required';
    if (form.periodChoice === 'months' && periodLength(form) === null) return 'login-limit-period-months-range';
    if (form.periodChoice === 'days' && periodLength(form) === null) return 'login-limit-period-days-range';
  }
  if (needsConnect) return 'login-limit-block-needs-connect';
  if (form.admitKind === 'custom') {
    const urlProblem = admitUrlProblem(form.admitUrl);
    if (urlProblem) return urlProblem;
  }
  if (messageProblem(form.customMessage)) return 'login-limit-message-invalid';
  if (form.alertEmail.trim() && !EMAIL_RE.test(form.alertEmail.trim())) return 'login-limit-alert-invalid';
  if (shownLabelProblem(form)) return 'login-limit-login-id-invalid';
  return null;
}

type ConnectIssue =
  | { kind: 'permissions'; action: 'connect' | 'disconnect'; permissions: string[]; roleHold: boolean }
  | { kind: 'other' };

function connectIssueFrom(error: unknown): ConnectIssue {
  if (error instanceof LoginLimitApiError && error.code === 'LOGIN_LIMIT_CONNECT_PERMISSIONS') {
    return { kind: 'permissions', action: 'connect', permissions: error.permissions ?? [], roleHold: error.roleHold };
  }
  if (error instanceof LoginLimitApiError && error.code === 'LOGIN_LIMIT_DISCONNECT_PERMISSIONS') {
    return { kind: 'permissions', action: 'disconnect', permissions: error.permissions ?? [], roleHold: false };
  }
  return { kind: 'other' };
}

function errorText(error: unknown, fallbackKey = 'login-limit-save-error'): { text: string; field?: string } {
  if (error instanceof SessionExpiredError) return { text: t('session-expired-inline') };
  if (error instanceof GenesysUnavailableError) return { text: t('error-genesys-unavailable') };
  if (error instanceof LoginLimitApiError) {
    if (error.code === 'LOGIN_LIMIT_CONNECT_PERMISSIONS') {
      return { text: t('login-limit-connect-permissions', { permissions: (error.permissions ?? []).join(', ') }) };
    }
    const known: Record<string, string> = {
      LOGIN_LIMIT_TYPE_REQUIRED: 'login-limit-type-required',
      LOGIN_LIMIT_LIMIT_INVALID: 'login-limit-limit-invalid',
      LOGIN_LIMIT_LIMIT_REQUIRED: 'login-limit-limit-required',
      LOGIN_LIMIT_PERIOD_REQUIRED: 'login-limit-period-required',
      LOGIN_LIMIT_ADMIT_URL_INVALID: 'login-limit-admit-url-invalid',
      LOGIN_LIMIT_ADMIT_URL_HTTPS: 'login-limit-admit-url-https',
      LOGIN_LIMIT_ADMIT_URL_CREDENTIALS: 'login-limit-admit-url-credentials',
      LOGIN_LIMIT_ADMIT_URL_MEDIABRIZ: 'login-limit-admit-url-mediabriz',
      LOGIN_LIMIT_ADMIT_URL_HOST_INVALID: 'login-limit-admit-url-host-invalid',
      LOGIN_LIMIT_MESSAGE_INVALID: 'login-limit-message-invalid',
      LOGIN_LIMIT_LABEL_INVALID: 'login-limit-login-id-invalid',
      LOGIN_LIMIT_ALLOWLIST_INVALID: 'login-limit-allowlist-invalid',
      LOGIN_LIMIT_ALERT_EMAIL_INVALID: 'login-limit-alert-invalid',
      LOGIN_LIMIT_LICENSE_TYPES_INVALID: 'login-limit-license-types-invalid',
      LOGIN_LIMIT_LICENSE_TYPES_ASK: 'login-limit-license-types-ask-locked',
      LOGIN_LIMIT_LICENSE_TYPE_NOT_OFFERED: 'login-limit-license-types-not-offered-error',
      LOGIN_LIMIT_LICENSE_TYPES_UNAVAILABLE: 'login-limit-license-types-unavailable-error',
      LOGIN_LIMIT_BLOCK_DISCONNECTED: 'login-limit-block-needs-connect',
      LOGIN_LIMIT_REPORT_CHECKING: 'login-limit-report-checking-error',
      LOGIN_LIMIT_PRIVATE_LINK: 'login-limit-link-missing',
      LOGIN_LIMIT_EXCEPTIONS_INVALID: 'login-limit-exceptions-invalid',
      LOGIN_LIMIT_TIME_ZONE_REQUIRED: 'login-limit-time-zone-required',
      LOGIN_LIMIT_UNAVAILABLE: 'login-limit-unavailable',
      LOGIN_LIMIT_GENESYS_UNAVAILABLE: 'login-limit-genesys-unavailable',
      LOGIN_LIMIT_NOT_ENABLED: 'login-limit-unavailable',
    };
    const key = error.code ? known[error.code] : undefined;
    return { text: key ? t(key) : t(fallbackKey), field: error.field };
  }
  return { text: t(fallbackKey) };
}

/**
 * Login Limit settings. Mounted only when Mediabriz has enabled the feature.
 * The Guard section on the same page is unchanged.
 */
export function LoginLimitSection({
  genesysOrgId,
  genesysRegion,
  first = false,
  threatMailbox = true,
  preview = false,
}: LoginLimitSectionProps) {
  const [scenario] = useState<PreviewScenario | null>(() => (preview ? previewScenario() : null));
  const [start] = useState<PreviewStart | null>(() => (scenario ? previewStart(scenario.loginLimitState) : null));
  const [loading, setLoading] = useState(!preview);
  const [hidden, setHidden] = useState(false);
  const [available, setAvailable] = useState(preview);
  const [reloading, setReloading] = useState(false);
  const [form, setForm] = useState<FormState>(start ? start.form : EMPTY_FORM);
  const [status, setStatus] = useState<LoginLimitStatus | null>(start ? start.status : null);
  const [loginUrl, setLoginUrl] = useState(preview ? PREVIEW_STATUS.loginUrl : null);
  const [threatEmail, setThreatEmail] = useState(preview && threatMailbox ? 'security@example.com' : '');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  const saveConfirmation = useSaveConfirmation();
  const [justVerified, setJustVerified] = useState(false);
  const [verifyCode, setVerifyCode] = useState<LoginLimitVerifyCode | 'unavailable' | null>(null);
  const [busy, setBusy] = useState<'save' | 'off' | 'connect' | 'disconnect' | 'verify' | 'private' | 'check' | null>(null);
  const [copied, setCopied] = useState(false);
  const [privateKey, setPrivateKey] = useState('');
  const [connectIssue, setConnectIssue] = useState<ConnectIssue | null>(null);
  const [cleanupIncomplete, setCleanupIncomplete] = useState(false);
  const [licenseTypeOptions, setLicenseTypeOptions] = useState<LicenseTypeOptions>(
    preview ? PREVIEW_LICENSE_TYPES : { state: 'unreadable', options: [] },
  );
  const [licenseRetrying, setLicenseRetrying] = useState(false);
  const [savedMode, setSavedMode] = useState<LoginLimitMode>(start ? start.savedMode : 'off');
  const [savedType, setSavedType] = useState<LoginLimitType | null>(start ? start.savedType : null);
  /** The stored Login ID label, sent instead of an invalid edit that was then hidden. */
  const [savedLoginIdLabel, setSavedLoginIdLabel] = useState(start ? start.form.loginIdLabel : '');
  /** Connect or Check again succeeded on this page, so a billing report is awaited. */
  const [connectedHere, setConnectedHere] = useState(false);
  /** Polling for the billing report ran its full 4 minutes. */
  const [reportWaitOver, setReportWaitOver] = useState(false);
  /** The report as it was when Check again was selected. See `loginLimitReportWait`. */
  const [retryBaseline, setRetryBaseline] = useState<string | null>(null);
  const [checkAgainError, setCheckAgainError] = useState<string | null>(null);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  /** Dev preview only: status reads so far, so headless checks can see the polling. */
  const [previewPolls, setPreviewPolls] = useState(0);
  const [turnOffError, setTurnOffError] = useState<string | null>(null);
  const [turnedOff, setTurnedOff] = useState(false);
  const typeTouched = useRef(false);
  /** The form as last loaded or saved, so only real edits are kept across a sign-in. */
  const loadedForm = useRef<FormState | null>(null);
  /** Edits kept across a sign-in, applied once the settings can be edited. */
  const pendingDraft = useRef<FormState | null>(null);
  const formRef = useRef(form);
  formRef.current = form;
  const sessionExpired = useSessionExpired();
  const refreshRef = useRef<() => void>(() => {});
  const pollingRef = useRef(false);
  const waitStartedAt = useRef<number | null>(null);
  const previewCheckQueued = useRef(false);
  const previewCheckStartedAt = useRef(Date.now());
  const verifyTimer = useRef<number | null>(null);
  const connectButton = useRef<HTMLButtonElement | null>(null);
  const focusConnectAfterBusy = useRef(false);

  const flashVerified = () => {
    if (verifyTimer.current) window.clearTimeout(verifyTimer.current);
    setJustVerified(true);
    verifyTimer.current = window.setTimeout(() => {
      verifyTimer.current = null;
      setJustVerified(false);
    }, SAVED_CONFIRMATION_MS);
  };

  useEffect(() => () => {
    if (verifyTimer.current) window.clearTimeout(verifyTimer.current);
  }, []);

  // Disconnect removes the focused button, so focus moves to Connect once it is enabled again.
  useEffect(() => {
    if (busy !== null || !focusConnectAfterBusy.current) return;
    focusConnectAfterBusy.current = false;
    connectButton.current?.focus();
  }, [busy]);

  const applyView = (view: Awaited<ReturnType<typeof getLoginLimit>>, replaceForm: boolean) => {
    if (!view.enabled) {
      setHidden(true);
      return;
    }
    setHidden(false);
    setAvailable(view.available);
    setStatus(view.status);
    setLoginUrl(view.loginUrl);
    setThreatEmail(view.threatNotifyEmail);
    setLicenseTypeOptions(view.licenseTypeOptions);
    if (replaceForm) setPrivateKey(view.privateKey);
    if (view.settings) {
      setSavedMode(view.settings.loginLimitMode);
      setSavedType(view.settings.loginLimitType);
      setSavedLoginIdLabel(view.settings.loginLimitLoginIdLabel);
    }
    if (replaceForm && view.settings) {
      typeTouched.current = view.settings.loginLimitType !== null;
      const settings = view.settings;
      loadedForm.current = formFromSettings(settings);
      setForm(loadedForm.current);
    }
  };

  useEffect(() => registerResumeDraft('login-limit-form', () => {
    if (pendingDraft.current) return pendingDraft.current;
    const loaded = loadedForm.current;
    if (!loaded || JSON.stringify(formRef.current) === JSON.stringify(loaded)) return null;
    return formRef.current;
  }), []);

  useEffect(() => {
    if (preview) return;
    let cancelled = false;
    (async () => {
      try {
        const view = await getLoginLimit(genesysOrgId, genesysRegion, true);
        if (cancelled) return;
        applyView(view, true);
        // Edits kept when the session expired come back unsaved, over the fresh settings.
        pendingDraft.current = view.enabled && view.settings ? resumeDraft<FormState>('login-limit-form') : null;
      } catch (error) {
        if (!cancelled) setLoadError(errorText(error, 'login-limit-load-error').text);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [genesysOrgId, genesysRegion, preview]);

  refreshRef.current = scenario
    ? () => {
      setPreviewPolls((count) => count + 1);
      // Like the gate: the read after Check again shows the new check running.
      if (previewCheckQueued.current) {
        previewCheckQueued.current = false;
        setStatus(PREVIEW_CHECKING_STATUS);
        return;
      }
      setStatus((current) => (
        current ? previewReportDone(current, scenario.loginLimitReport, previewCheckStartedAt.current) : current
      ));
    }
    : () => {
      void getLoginLimit(genesysOrgId, genesysRegion, false)
        .then((view) => applyView(view, false))
        .catch(() => undefined);
    };

  const {
    connected,
    locked,
    checkLocked,
    waitingForReport,
    slowPoll,
    detectedType,
    typeChecking,
    typeHelpKey,
    canCheckAgain,
    retryAt,
  } = loginLimitReportWait({ status, savedType, connectedHere, reportWaitOver, retryBaseline });
  pollingRef.current = waitingForReport || slowPoll;

  // A kept draft waits while the settings are locked, then never overrides a type the report set.
  useEffect(() => {
    const draft = pendingDraft.current;
    if (!draft || loading || locked || checkLocked) return;
    pendingDraft.current = null;
    const typeLocked = detectedType !== null || typeChecking;
    if (!typeLocked) typeTouched.current = typeTouched.current || draft.type !== '';
    setForm((current) => ({
      ...current,
      ...draft,
      type: typeLocked ? current.type : draft.type,
      licenseTypesKnown: current.licenseTypesKnown,
      exceptionsStored: current.exceptionsStored,
    }));
  }, [loading, locked, checkLocked, detectedType, typeChecking]);

  // Polling pauses while the tab is hidden, and reads the status once when it is shown again.
  useEffect(() => {
    const onVisibility = () => {
      const visible = document.visibilityState !== 'hidden';
      setPageVisible(visible);
      if (visible && pollingRef.current && !isSessionExpired()) refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (!waitingForReport) {
      waitStartedAt.current = null;
      return;
    }
    // Hidden time still counts toward the 4 minutes.
    waitStartedAt.current ??= Date.now();
    // An expired session stops the wait; the session-expired dialog takes over.
    if (!pageVisible || sessionExpired) return;
    const started = waitStartedAt.current;
    const timer = window.setInterval(() => {
      if (Date.now() - started >= REPORT_POLL_MS) {
        window.clearInterval(timer);
        setReportWaitOver(true);
        return;
      }
      refreshRef.current();
    }, REPORT_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [waitingForReport, pageVisible, sessionExpired]);

  useEffect(() => {
    if (!slowPoll || !pageVisible || sessionExpired) return;
    const timer = window.setInterval(() => refreshRef.current(), REPORT_SLOW_POLL_MS);
    return () => window.clearInterval(timer);
  }, [slowPoll, pageVisible, sessionExpired]);

  useEffect(() => {
    if (typeTouched.current || form.type) return;
    if (status?.report?.hourlyLoggedInWarning) {
      setForm((current) => current.type ? current : { ...current, type: 'hours' });
      return;
    }
    const model = status?.report?.model;
    if (model === 'concurrent' || model === 'named') {
      setForm((current) => current.type ? current : { ...current, type: model });
    }
  }, [status, form.type]);

  useEffect(() => {
    if (!detectedType) return;
    setForm((current) => (current.type === detectedType ? current : { ...current, type: detectedType }));
  }, [detectedType, form.type]);

  const zoneShown = zoneNeeded(form);
  useEffect(() => {
    if (!zoneShown || form.reportTimeZone) return;
    const zone = browserTimeZone();
    setForm((current) => (
      zoneNeeded(current) && !current.reportTimeZone ? { ...current, reportTimeZone: zone } : current
    ));
  }, [zoneShown, form.reportTimeZone]);

  if (hidden) return null;

  const report = status?.report;
  const blockLocksAsk = form.type !== 'named' && form.licenseTypes.some((row) => row.action === 'block');
  // The label is only shown when the page asks. A hidden field keeps its saved value.
  const pageAsks = loginIdLabelShown(form);
  const needsConnect = !connected && saveNeedsConnection(savedMode, form.mode);
  const block = problemKey(form, needsConnect);
  const requiresChoices = form.mode === 'monitor' || form.mode === 'block';
  const typeMissing = requiresChoices && form.type === '' && limitNumber(form.limit) !== null;
  const limitKey = requiresChoices ? limitProblemKey(form) : null;
  const limitMissing = limitKey !== null;
  const startMissing = requiresChoices && usesPeriod(form.type) && form.periodStart === '';
  const lengthMissing = requiresChoices && usesPeriod(form.type) && form.periodChoice !== 'one' && periodLength(form) === null;
  const hoursBudget = form.type === 'hours';
  const hoursOffered = hoursBudget || preview || status?.report?.hourlyLoggedInWarning === true;
  const slug = status?.slug ?? '';
  const privateOn = privateKey !== '' || status?.privateLink === true;
  const origin = loginOrigin(loginUrl);
  const link = slug && privateKey
    ? `${origin}/${slug}/${privateKey}`
    : (loginUrl || (slug ? `${origin}/${slug}` : ''));
  const showPrivateNudge = form.mode === 'block' && form.allowlist.split(/\r?\n/).every((line) => line.trim() === '');
  const activePeriodLength = periodLength(form);
  const period = usesPeriod(form.type) && form.periodStart && activePeriodLength
    ? currentBillingPeriod(form.periodStart, form.periodUnit, activePeriodLength)
    : null;
  const periodText = period ? periodPreviewText(period) : '';

  const patch = (next: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...next }));
    saveConfirmation.clear();
    setSaveError(null);
    setErrorField(null);
  };

  const confirmAskForBlock = () => confirmDialog({
    title: t('login-limit-license-types-confirm-title'),
    message: t('login-limit-license-types-confirm'),
    confirmLabel: t('login-limit-license-types-confirm-action'),
    cancelLabel: t('confirm-dialog-cancel'),
  });

  const onType = async (type: FormState['type']) => {
    const needsAsk = type !== 'named'
      && form.licenseTypes.some((row) => row.action === 'block')
      && !form.askForLoginId;
    if (needsAsk && !await confirmAskForBlock()) return;
    typeTouched.current = true;
    patch({
      ...(needsAsk ? { type, askForLoginId: true as const } : { type }),
      ...(type === 'named' && form.exceptionsReport && !form.reportTimeZone
        ? { reportTimeZone: browserTimeZone() }
        : {}),
    });
  };

  const onLicenseAction = async (index: number, action: LicenseTypeLimitAction) => {
    const row = form.licenseTypes[index];
    if (!row || row.action === action) return;
    const needsAsk = action === 'block' && form.type !== 'named' && !form.askForLoginId;
    if (needsAsk && !await confirmAskForBlock()) return;
    const licenseTypes = form.licenseTypes.map((item, itemIndex) => (
      itemIndex === index ? { ...item, action } : item
    ));
    patch(needsAsk ? { licenseTypes, askForLoginId: true } : { licenseTypes });
  };

  const addLicenseType = () => {
    if (licenseTypeOptions.state !== 'ready' || form.licenseTypes.length >= MAX_LICENSE_TYPE_ROWS) return;
    const used = new Set(form.licenseTypes.map((row) => row.id));
    const id = licenseTypeOptions.options.find((option) => !used.has(option.id))?.id;
    if (!id) return;
    patch({ licenseTypes: [...form.licenseTypes, { id, limit: '', action: 'alert' }] });
  };

  /** The server also asks the gate to read the license types again, so a few polls follow. */
  const onRetryLicenseTypes = async () => {
    if (licenseRetrying) return;
    setLicenseRetrying(true);
    try {
      if (preview) {
        setLicenseTypeOptions(PREVIEW_LICENSE_TYPES);
        return;
      }
      let view = await getLoginLimit(genesysOrgId, genesysRegion, false, true);
      applyView(view, false);
      for (let attempt = 0; attempt < 3 && view.enabled && view.licenseTypeOptions.state === 'unreadable'; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 5000));
        view = await getLoginLimit(genesysOrgId, genesysRegion, false);
        applyView(view, false);
      }
    } catch {
      // The unreadable reason stays on screen with Retry.
    } finally {
      setLicenseRetrying(false);
    }
  };

  /** `replaceForm` false keeps unsaved edits; Connect and Disconnect do not change the saved fields. */
  const reload = async (replaceForm = true) => {
    const view = await getLoginLimit(genesysOrgId, genesysRegion, false);
    applyView(view, replaceForm);
  };

  /** Retry after a failed load or while the gate is unavailable. Edits survive unless the load had failed. */
  const onRetryLoad = async () => {
    if (reloading) return;
    setReloading(true);
    try {
      const view = await getLoginLimit(genesysOrgId, genesysRegion, false);
      applyView(view, loadError !== null);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorText(error, 'login-limit-load-error').text);
    } finally {
      setReloading(false);
    }
  };

  const onSave = async () => {
    if (block || busy) return;
    setBusy('save');
    setSaveError(null);
    setConnectionError(null);
    saveConfirmation.clear();
    try {
      if (!preview) {
        const saved = await saveLoginLimit(genesysOrgId, genesysRegion, saveBody(form, savedLoginIdLabel));
        loadedForm.current = formFromSettings(saved);
        setForm(loadedForm.current);
        setSavedMode(saved.loginLimitMode);
        setSavedType(saved.loginLimitType);
        setSavedLoginIdLabel(saved.loginLimitLoginIdLabel);
        await reload().catch(() => undefined);
      }
      saveConfirmation.confirm(t('settings-save-success'));
    } catch (error) {
      saveConfirmation.clear();
      const parsed = errorText(error);
      setSaveError(parsed.text);
      setErrorField(parsed.field ?? null);
    } finally {
      setBusy(null);
    }
  };

  /** The one save allowed while not connected or checking. The server keeps every other stored field. */
  const onTurnOff = async () => {
    if (busy) return;
    setBusy('off');
    setTurnOffError(null);
    setTurnedOff(false);
    try {
      if (!preview) {
        const saved = await saveLoginLimit(genesysOrgId, genesysRegion, { loginLimitMode: 'off' });
        loadedForm.current = formFromSettings(saved);
        setForm(loadedForm.current);
        setSavedMode(saved.loginLimitMode);
        setSavedType(saved.loginLimitType);
        await reload().catch(() => undefined);
      } else {
        setForm((current) => ({ ...current, mode: 'off' }));
        setSavedMode('off');
      }
      setTurnedOff(true);
    } catch (error) {
      setTurnOffError(errorText(error).text);
    } finally {
      setBusy(null);
    }
  };

  const onConnect = async () => {
    if (busy) return;
    setBusy('connect');
    setSaveError(null);
    setConnectionError(null);
    setConnectIssue(null);
    setVerifyCode(null);
    setJustVerified(false);
    setCleanupIncomplete(false);
    setTurnOffError(null);
    setTurnedOff(false);
    try {
      if (!preview) {
        await connectLoginLimit(genesysOrgId, genesysRegion);
        await reload(false);
      } else {
        setStatus(PREVIEW_CHECKING_STATUS);
        setLoginUrl(PREVIEW_STATUS.loginUrl);
        previewCheckStartedAt.current = Date.now();
      }
      setRetryBaseline(null);
      setCheckAgainError(null);
      setReportWaitOver(false);
      setConnectedHere(true);
    } catch (error) {
      // A refused Connect does not change the connection status.
      const issue = connectIssueFrom(error);
      if (issue.kind === 'permissions') setConnectIssue(issue);
      else if (error instanceof SessionExpiredError || error instanceof GenesysUnavailableError) {
        setConnectionError(errorText(error).text);
      } else if (error instanceof LoginLimitApiError && error.code === 'LOGIN_LIMIT_GENESYS_UNAVAILABLE') {
        setConnectionError(t('login-limit-genesys-unavailable'));
      } else setConnectionError(t('login-limit-connect-error'));
    } finally {
      setBusy(null);
    }
  };

  /** Asks the gate to read the billing report now, then waits for it as after Connect. */
  const onCheckAgain = async () => {
    if (busy) return;
    setBusy('check');
    setCheckAgainError(null);
    const baseline = reportKey(status?.report);
    try {
      if (!preview) {
        const view = await getLoginLimit(genesysOrgId, genesysRegion, false, false, true);
        applyView(view, false);
        if (view.reportCheckStarted !== true) {
          setCheckAgainError(t('login-limit-check-again-error'));
          return;
        }
      } else {
        previewCheckQueued.current = true;
        previewCheckStartedAt.current = Date.now();
      }
      setRetryBaseline(baseline);
      setReportWaitOver(false);
      setConnectedHere(true);
    } catch (error) {
      setCheckAgainError(errorText(error, 'login-limit-check-again-error').text);
    } finally {
      setBusy(null);
    }
  };

  const onDisconnect = async () => {
    if (busy) return;
    const confirmed = await confirmDialog({
      title: t('login-limit-disconnect-title'),
      message: t('login-limit-disconnect-message'),
      confirmLabel: t('login-limit-disconnect-confirm'),
      cancelLabel: t('confirm-dialog-cancel'),
      danger: true,
    });
    if (!confirmed) return;
    setBusy('disconnect');
    setSaveError(null);
    setConnectionError(null);
    setVerifyCode(null);
    setJustVerified(false);
    setConnectIssue(null);
    setCleanupIncomplete(false);
    try {
      if (!preview) {
        const result = await disconnectLoginLimit(genesysOrgId, genesysRegion);
        setCleanupIncomplete(!result.genesysCleanupComplete);
        await reload(false);
      } else {
        setStatus({ ...PREVIEW_STATUS, connection: 'disconnected', report: null, hourlyInteractingExcluded: null });
      }
      setConnectedHere(false);
      setReportWaitOver(false);
      setRetryBaseline(null);
      setCheckAgainError(null);
      focusConnectAfterBusy.current = true;
    } catch (error) {
      // A Disconnect refused for permissions has not changed anything.
      const issue = connectIssueFrom(error);
      if (issue.kind === 'permissions') setConnectIssue(issue);
      else setConnectionError(errorText(error, 'login-limit-disconnect-error').text);
    } finally {
      setBusy(null);
    }
  };

  const onVerify = async () => {
    if (busy || justVerified) return;
    setBusy('verify');
    setVerifyCode(null);
    setJustVerified(false);
    try {
      const result = preview ? { ok: true as const, code: 'ok' as const } : await verifyLoginLimit(genesysOrgId, genesysRegion);
      setVerifyCode(result.code);
      if (result.ok) flashVerified();
    } catch {
      setVerifyCode('unavailable');
    } finally {
      setBusy(null);
    }
  };

  const applyPrivate = (enabled: boolean, key: string) => {
    setPrivateKey(enabled ? key : '');
    if (status) setStatus({ ...status, privateLink: enabled });
  };

  const onPrivate = async (enabled: boolean) => {
    if (busy || !slug) return;
    setBusy('private');
    setSaveError(null);
    try {
      if (preview) {
        applyPrivate(enabled, enabled ? previewPrivateKey() : '');
        return;
      }
      const result = await setLoginLimitPrivateLink(genesysOrgId, genesysRegion, enabled);
      applyPrivate(result.enabled, result.key);
      if (!result.saved) setSaveError(t('login-limit-private-unsaved'));
    } catch (error) {
      setSaveError(errorText(error).text);
    } finally {
      setBusy(null);
    }
  };

  const onNewPrivateLink = async () => {
    if (busy || !slug) return;
    const confirmed = await confirmDialog({
      title: t('login-limit-private-confirm-title'),
      message: t('login-limit-private-confirm'),
      confirmLabel: t('login-limit-private-confirm-action'),
      cancelLabel: t('confirm-dialog-cancel'),
    });
    if (!confirmed) return;
    await onPrivate(true);
  };

  const onCopy = async () => {
    if (!link) return;
    await copyText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const typeChanged = detectedType && savedType && savedType !== detectedType
    ? t(`login-limit-type-changed-${detectedType}`)
    : '';
  const cxTier = report?.tier ?? status?.definitionTier ?? null;
  const limitHelp = hoursBudget
    ? t('login-limit-hours-help')
    : cxTier
      ? t(report?.tier && report.digital ? 'login-limit-limit-help-tier-digital' : 'login-limit-limit-help-tier', { tier: String(cxTier) })
      : t('login-limit-limit-help');
  const admitUrlKey = form.admitKind === 'custom' && (form.mode !== 'off' || form.admitUrl.trim() !== '')
    ? admitUrlProblem(form.admitUrl)
    : null;
  // One reason at a time, under the field: the local check first, else the server's specific refusal.
  const admitUrlServerError = form.admitKind === 'custom' && errorField === 'admitUrl' ? saveError : null;
  const admitUrlError = admitUrlKey ? t(admitUrlKey) : admitUrlServerError;
  const reportLine = waitingForReport
    ? t('login-limit-checking')
    : report?.model && report.tier
      ? t(
        `login-limit-reports-${report.model}${report.digital ? '-digital' : ''}`,
        { tier: String(report.tier) },
      )
      : !report?.model && status?.definitionTier
        ? t('login-limit-definition-tier', { tier: String(status.definitionTier) })
        : '';
  const locale = getCurrentLocale();
  const updated = report?.updatedAt
    ? t('login-limit-report-updated', { time: formatLocalTime(report.updatedAt, locale, true) })
    : connected && !waitingForReport
      ? t('login-limit-report-missing')
      : '';
  const reportDelayed = report?.delayed && !waitingForReport
    ? report.retryAt && formatLocalTime(report.retryAt, locale, true)
      ? t('login-limit-report-delayed', { time: formatLocalTime(report.retryAt, locale, true) })
      : t('login-limit-report-delayed-soon')
    : '';
  const genesysDown = connected && status?.genesysUnavailable === true;
  const genesysFailedAt = genesysDown && status?.genesysLastFailureAt
    ? formatLocalTime(status.genesysLastFailureAt, locale, true)
    : '';

  return (
    <section
      className={`settings-section${first ? ' settings-section--first' : ''}`}
      data-preview-polls={preview ? previewPolls : undefined}
    >
      <h3 className="settings-section__title">{t('login-limit-title')}</h3>
      <p className="settings-section__intro">{t('login-limit-intro')}</p>
      {LOGIN_LIMIT_HELP_URL && (
        <p className="actions-help login-limit__guide">
          <RichText
            k="login-limit-guide"
            values={{
              guide: (
                <a
                  href={LOGIN_LIMIT_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('login-limit-guide-link-label')}
                >
                  {t('login-limit-guide-link')}
                </a>
              ),
            }}
          />
        </p>
      )}

      {loading && <p className="settings-page__status">{t('login-limit-loading')}</p>}
      {!loading && loadError && <p className="settings-page__error" role="alert">{loadError}</p>}

      {!loading && !loadError && !available && (
        <p className="settings-page__notice" role="status">{t('login-limit-unavailable')}</p>
      )}

      {!loading && (loadError || !available) && (
        <div className="login-limit__actions">
          <button type="button" className="btn btn--secondary" onClick={onRetryLoad} disabled={reloading} aria-busy={reloading}>
            {reloading && <span className="btn-spinner" aria-hidden="true" />}
            {t('login-limit-retry')}
          </button>
        </div>
      )}

      {!loading && !loadError && available && (
        <>
          <div className="form-group">
            <p className="form-label">{t('login-limit-connection-label')}</p>
            <div className="login-limit__status">
              <span className="login-limit__status-state">{t(connected ? 'login-limit-connected' : 'login-limit-not-connected')}</span>
              {connected && status?.license?.countingAllUsers && (
                <span>{t('login-limit-counting-all')}</span>
              )}
              {(reportLine || updated) && (
                <span className={waitingForReport ? 'login-limit__checking' : undefined}>
                  {waitingForReport && <span className="btn-spinner" aria-hidden="true" />}
                  {[reportLine, updated].filter(Boolean).join(' · ')}
                </span>
              )}
              {connected && (status?.hourlyInteractingExcluded ?? 0) > 0 && (
                <span>{t('login-limit-hourly-excluded', { count: String(status?.hourlyInteractingExcluded) })}</span>
              )}
              {reportDelayed && <span>{reportDelayed}</span>}
            </div>
            {genesysDown && (
              <div className="settings-page__notice settings-page__notice--warning login-limit__genesys-down" role="status">
                <p>{t('login-limit-genesys-down')}</p>
                {genesysFailedAt && <p>{t('login-limit-genesys-last-failure', { time: genesysFailedAt })}</p>}
                <div className="login-limit__actions">
                  <button type="button" className="btn btn--secondary" onClick={onRetryLoad} disabled={reloading} aria-busy={reloading}>
                    {reloading && <span className="btn-spinner" aria-hidden="true" />}
                    {t('login-limit-retry')}
                  </button>
                </div>
              </div>
            )}
            {!connected && <p className="form-hint">{t('login-limit-connect-why')}</p>}
            {connectIssue?.kind === 'permissions' && (
              <div role="alert">
                <p className="settings-page__error">
                  {t(connectIssue.action === 'disconnect'
                    ? 'login-limit-disconnect-missing'
                    : connectIssue.roleHold && connectIssue.permissions.length === 0
                      ? 'login-limit-connect-role-hold'
                      : 'login-limit-connect-missing')}
                </p>
                {connectIssue.permissions.length > 0 && (
                  <>
                    <p className="form-hint">{t('error-missing-permissions')}</p>
                    <ul className="login-limit__permissions" dir="ltr">
                      {connectIssue.permissions.map((permission) => (
                        <li key={permission}>{permission}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
            {report?.hourlyLoggedInWarning && (
              <p className="settings-page__notice settings-page__notice--warning" role="status">
                {t('login-limit-hourly-warning')}
              </p>
            )}
            <div className="login-limit__actions">
              <button
                ref={connectButton}
                type="button"
                className="btn btn--primary"
                onClick={() => void onConnect()}
                disabled={busy !== null}
              >
                {busy === 'connect'
                  ? t('login-limit-connecting')
                  : t(connected ? 'login-limit-reconnect' : 'login-limit-connect')}
              </button>
              {(connected || status?.connection === 'invalid_client') && (
                <button type="button" className="btn btn--danger" onClick={() => void onDisconnect()} disabled={busy !== null}>
                  {busy === 'disconnect' ? t('login-limit-disconnecting') : t('login-limit-disconnect')}
                </button>
              )}
              {(connected || status?.connection === 'invalid_client') && (
                <SaveButton
                  variant="secondary"
                  saving={busy === 'verify'}
                  justSaved={justVerified}
                  label={t('login-limit-verify')}
                  savingLabel={t('login-limit-verifying')}
                  savedLabel={t('login-limit-verified')}
                  onClick={() => void onVerify()}
                  disabled={busy !== null}
                />
              )}
            </div>
            {connectionError && <p className="settings-page__error" role="alert">{connectionError}</p>}
            {cleanupIncomplete && (
              <p className="settings-page__notice settings-page__notice--warning" role="status">
                {t('login-limit-disconnect-cleanup')}
              </p>
            )}
            {verifyCode === 'ok' && (
              <p className="settings-page__success" role="status">{t('login-limit-verify-ok')}</p>
            )}
            {verifyCode === 'genesys_unreachable' && (
              <p className="settings-page__notice settings-page__notice--warning" role="status">{t('login-limit-verify-unreachable')}</p>
            )}
            {verifyCode === 'unavailable' && (
              <p className="settings-page__error" role="alert">{t('login-limit-unavailable')}</p>
            )}
            {(verifyCode === 'invalid_client' || verifyCode === 'missing_role' || verifyCode === 'missing_permission') && (
              <p className="settings-page__error" role="alert">{t(`login-limit-verify-${verifyCode.replace(/_/g, '-')}`)}</p>
            )}
          </div>

          {(locked || checkLocked) && (
            <div className="login-limit__locked-head">
              {locked ? (
                <p id="ll-locked-title" className="login-limit__locked-title">{t('login-limit-locked-title')}</p>
              ) : (
                <>
                  <p id="ll-locked-title" className="login-limit__locked-title login-limit__checking" role="status">
                    <span className="btn-spinner" aria-hidden="true" />
                    {t('login-limit-checking')}
                  </p>
                  <p className="form-hint">{t('login-limit-checking-hint')}</p>
                </>
              )}
              {savedMode !== 'off' && (
                <>
                  <p className="form-hint">{t(locked ? 'login-limit-locked-on' : 'login-limit-checking-on')}</p>
                  <div className="login-limit__actions">
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => void onTurnOff()}
                      disabled={busy !== null}
                      aria-busy={busy === 'off'}
                    >
                      {busy === 'off' ? (
                        <>
                          <span className="btn-spinner" aria-hidden="true" />
                          {t('settings-saving')}
                        </>
                      ) : t('login-limit-turn-off')}
                    </button>
                  </div>
                </>
              )}
              {turnedOff && savedMode === 'off' && (
                <p className="settings-page__success" role="status">{t('login-limit-turned-off')}</p>
              )}
              {turnOffError && <p className="settings-page__error" role="alert">{turnOffError}</p>}
            </div>
          )}

          <fieldset
            className={`login-limit__settings${locked || checkLocked ? ' login-limit__settings--locked' : ''}`}
            disabled={locked || checkLocked}
            aria-labelledby={locked || checkLocked ? 'll-locked-title' : undefined}
          >
            <fieldset className="login-limit__fieldset">
              <legend className="form-label">{t('login-limit-mode-label')}</legend>
              {(['off', 'monitor', 'block'] as const).map((mode) => (
                <label key={mode} className="login-limit__choice">
                  <input
                    type="radio"
                    name="login-limit-mode"
                    checked={form.mode === mode}
                    onChange={() => patch({ mode })}
                  />
                  <span>{t(`login-limit-mode-${mode}`)}</span>
                </label>
              ))}
              <p className="form-hint">{t(`login-limit-mode-help-${form.mode}`)}</p>
            </fieldset>

            <div className="form-group">
              <label className="form-label" htmlFor="ll-type">{t('login-limit-type-label')}</label>
              <select
                id="ll-type"
                className={`form-select${typeMissing ? ' form-input--invalid' : ''}`}
                aria-invalid={typeMissing || undefined}
                disabled={detectedType !== null || typeChecking}
                aria-busy={typeChecking || undefined}
                value={form.type}
                onChange={(event) => {
                  void onType(event.target.value as FormState['type']);
                }}
              >
                <option value="">{t(typeChecking ? 'login-limit-checking' : 'login-limit-type-required')}</option>
                <option value="concurrent">{t('login-limit-type-concurrent')}</option>
                <option value="named">{t('login-limit-type-named')}</option>
                {hoursOffered && <option value="hours">{t('login-limit-type-hours')}</option>}
              </select>
              {typeChecking ? (
                <p className="form-hint login-limit__checking">
                  <span className="btn-spinner" aria-hidden="true" />
                  {t(typeHelpKey)}
                </p>
              ) : (
                <p className="form-hint">{t(typeHelpKey)}</p>
              )}
              {canCheckAgain && (
                <div className="login-limit__check-again">
                  {retryAt && formatLocalTime(retryAt, locale, true) && (
                    <p className="form-hint">{t('login-limit-report-retry-at', { time: formatLocalTime(retryAt, locale, true) })}</p>
                  )}
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => void onCheckAgain()}
                    disabled={busy !== null}
                    aria-busy={busy === 'check' || undefined}
                  >
                    {busy === 'check' && <span className="btn-spinner" aria-hidden="true" />}
                    {t('login-limit-check-again')}
                  </button>
                </div>
              )}
              {checkAgainError && <p className="form-error" role="alert">{checkAgainError}</p>}
              {typeChanged && (
                <p className="settings-page__notice settings-page__notice--warning login-limit__inline-notice" role="status">{typeChanged}</p>
              )}
              {typeMissing && <p className="form-error" role="alert">{t('login-limit-type-required')}</p>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="ll-limit">
                {t(hoursBudget ? 'login-limit-hours-label' : 'login-limit-limit-label')}
              </label>
              <NumberStepper
                id="ll-limit"
                invalid={limitMissing}
                disabled={!form.type}
                value={form.limit}
                decreaseLabel={t(hoursBudget ? 'login-limit-hours-decrease' : 'login-limit-limit-decrease')}
                increaseLabel={t(hoursBudget ? 'login-limit-hours-increase' : 'login-limit-limit-increase')}
                onChange={(value) => patch({ limit: digitsOnly(value) })}
              />
              <p className="form-hint">{limitHelp}</p>
              {!hoursBudget && <p className="form-hint">{t('login-limit-limit-optional')}</p>}
              {limitKey && <p className="form-error" role="alert">{t(limitKey)}</p>}
            </div>

            <LicenseTypeLimits
              rows={form.licenseTypes}
              options={licenseTypeOptions}
              named={form.type === 'named'}
              retrying={licenseRetrying}
              onRetry={() => void onRetryLicenseTypes()}
              onLimit={(index, limit) => patch({
                licenseTypes: form.licenseTypes.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, limit: digitsOnly(limit) } : row
                )),
              })}
              onType={(index, id) => patch({
                licenseTypes: form.licenseTypes.map((row, rowIndex) => (rowIndex === index ? { ...row, id } : row)),
              })}
              onAction={(index, action) => void onLicenseAction(index, action)}
              onRemove={(index) => patch({
                licenseTypes: form.licenseTypes.filter((_, rowIndex) => rowIndex !== index),
                licenseTypesKnown: true,
              })}
              onAdd={addLicenseType}
            />

            {usesPeriod(form.type) && (
              <fieldset className="login-limit__fieldset">
                <legend className="form-label">{t('login-limit-period-label')}</legend>
                <div className="login-limit__period-date">
                  <label className="form-label" htmlFor="ll-period-start">{t('login-limit-period-start')}</label>
                  <input
                    id="ll-period-start"
                    className={`form-input login-limit__period-start${startMissing ? ' form-input--invalid' : ''}`}
                    aria-invalid={startMissing || undefined}
                    type="date"
                    dir="ltr"
                    value={form.periodStart}
                    onChange={(event) => patch({ periodStart: event.target.value })}
                  />
                </div>
                <p className="form-hint">{t('login-limit-period-where')}</p>
                {form.periodChoice !== 'days' && <p className="form-hint">{t('login-limit-period-help')}</p>}
                {hoursBudget && <p className="form-hint">{t('login-limit-hours-period-help')}</p>}
                {periodText && <p className="form-hint">{periodText}</p>}
                {startMissing && <p className="form-error" role="alert">{t('login-limit-period-required')}</p>}
                <div className="login-limit__period-choices" role="radiogroup" aria-label={t('login-limit-period-label')}>
                  <label className="login-limit__choice">
                    <input
                      type="radio"
                      name="ll-period-kind"
                      checked={form.periodChoice === 'one'}
                      onChange={() => patch({ periodChoice: 'one', periodUnit: 'month', periodLength: '1' })}
                    />
                    <span>{t('login-limit-period-one')}</span>
                  </label>
                  <label className="login-limit__choice">
                    <input
                      type="radio"
                      name="ll-period-kind"
                      checked={form.periodChoice === 'months'}
                      onChange={() => patch({
                        periodChoice: 'months',
                        periodUnit: 'month',
                        periodLength: form.periodChoice === 'months' ? form.periodLength : '',
                      })}
                    />
                    <span>{t('login-limit-period-months-choice')}</span>
                  </label>
                  {form.periodChoice === 'months' && (
                    <>
                      <input
                        id="ll-period-length"
                        className={`form-input login-limit__period-length${lengthMissing ? ' form-input--invalid' : ''}`}
                        inputMode="numeric"
                        dir="ltr"
                        aria-invalid={lengthMissing || undefined}
                        aria-label={t('login-limit-period-months-choice')}
                        value={form.periodLength}
                        onBeforeInput={(event) => {
                          const data = event.data;
                          const el = event.currentTarget;
                          const next = form.periodLength.slice(0, el.selectionStart ?? form.periodLength.length)
                            + (data ?? '')
                            + form.periodLength.slice(el.selectionEnd ?? form.periodLength.length);
                          if (data != null && !periodTextCanBecomeValid(next, PERIOD_MONTHS.min, PERIOD_MONTHS.max)) {
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) => patch({
                          periodLength: clampPeriodText(event.target.value, PERIOD_MONTHS.min, PERIOD_MONTHS.max),
                        })}
                      />
                      <p className={lengthMissing ? 'form-error' : 'form-hint'} role={lengthMissing ? 'alert' : undefined}>
                        {t('login-limit-period-months-range')}
                      </p>
                    </>
                  )}
                  <label className="login-limit__choice">
                    <input
                      type="radio"
                      name="ll-period-kind"
                      checked={form.periodChoice === 'days'}
                      onChange={() => patch({
                        periodChoice: 'days',
                        periodUnit: 'day',
                        periodLength: form.periodChoice === 'days' ? form.periodLength : '',
                      })}
                    />
                    <span>{t('login-limit-period-days-choice')}</span>
                  </label>
                  {form.periodChoice === 'days' && (
                    <>
                      <input
                        id="ll-period-length"
                        className={`form-input login-limit__period-length${lengthMissing ? ' form-input--invalid' : ''}`}
                        inputMode="numeric"
                        dir="ltr"
                        aria-invalid={lengthMissing || undefined}
                        aria-label={t('login-limit-period-days-choice')}
                        value={form.periodLength}
                        onBeforeInput={(event) => {
                          const data = event.data;
                          const el = event.currentTarget;
                          const next = form.periodLength.slice(0, el.selectionStart ?? form.periodLength.length)
                            + (data ?? '')
                            + form.periodLength.slice(el.selectionEnd ?? form.periodLength.length);
                          if (data != null && !periodTextCanBecomeValid(next, PERIOD_DAYS.min, PERIOD_DAYS.max)) {
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) => patch({
                          periodLength: clampPeriodText(event.target.value, PERIOD_DAYS.min, PERIOD_DAYS.max),
                        })}
                      />
                      <p className={lengthMissing ? 'form-error' : 'form-hint'} role={lengthMissing ? 'alert' : undefined}>
                        {t('login-limit-period-days-range')}
                      </p>
                    </>
                  )}
                </div>
              </fieldset>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="ll-link">{t('login-limit-link-label')}</label>
              {link ? (
                <div className="login-limit__link">
                  <input id="ll-link" className="form-input" dir="ltr" readOnly value={link} />
                  <button type="button" className="btn btn--secondary" onClick={() => void onCopy()}>
                    {copied ? t('login-limit-link-copied') : t('login-limit-link-copy')}
                  </button>
                </div>
              ) : (
                <p className="form-hint">{t('login-limit-link-missing')}</p>
              )}
              <p className="form-hint">{t('login-limit-link-help')}</p>
              {showPrivateNudge && !privateOn && (
                <p className="settings-page__notice settings-page__notice--warning login-limit__inline-notice" role="status">
                  {t('login-limit-private-nudge')}
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="settings-page__toggle">
                <input
                  type="checkbox"
                  checked={privateOn}
                  disabled={!slug || busy !== null}
                  onChange={(event) => void onPrivate(event.target.checked)}
                />
                <span>{t('login-limit-private-label')}</span>
              </label>
              <p className="form-hint">{t('login-limit-private-help')}</p>
              {!slug && <p className="form-hint">{t('login-limit-link-missing')}</p>}
              {privateOn && !privateKey && (
                <p className="form-hint">{t('login-limit-private-missing')}</p>
              )}
              {privateOn && (
                <>
                  <button
                    type="button"
                    className="btn btn--secondary login-limit__private-new"
                    onClick={() => void onNewPrivateLink()}
                    disabled={busy !== null}
                    aria-describedby="ll-private-new-help"
                  >
                    {t('login-limit-private-new')}
                  </button>
                  <p id="ll-private-new-help" className="form-hint">{t('login-limit-private-new-help')}</p>
                </>
              )}
            </div>

            <fieldset className="login-limit__fieldset">
              <legend className="form-label">{t('login-limit-admit-label')}</legend>
              {(['workspace', 'custom', 'other'] as const).map((kind) => (
                <label key={kind} className="login-limit__choice">
                  <input
                    type="radio"
                    name="login-limit-admit"
                    checked={form.admitKind === kind}
                    onChange={() => patch({ admitKind: kind })}
                  />
                  <span>{t(`login-limit-admit-${kind}`)}</span>
                </label>
              ))}
              <p className="form-hint">{t('login-limit-admit-help')}</p>
              {form.admitKind === 'custom' && (
                <>
                  <label className="form-label" htmlFor="ll-admit-url">{t('login-limit-admit-url-label')}</label>
                  <input
                    id="ll-admit-url"
                    className={`form-input${admitUrlError ? ' form-input--invalid' : ''}`}
                    type="url"
                    dir="ltr"
                    aria-invalid={admitUrlError ? true : undefined}
                    aria-describedby={admitUrlError ? 'll-admit-url-error' : undefined}
                    placeholder={t('login-limit-admit-url-placeholder')}
                    value={form.admitUrl}
                    onChange={(event) => patch({ admitUrl: event.target.value })}
                  />
                  {admitUrlError && (
                    <p id="ll-admit-url-error" className="form-error" role="alert">{admitUrlError}</p>
                  )}
                </>
              )}
            </fieldset>

            <div className="form-group">
              <label className="form-label" htmlFor="ll-message">{t('login-limit-message-label')}</label>
              <textarea
                id="ll-message"
                className="form-input form-textarea"
                dir={fieldDir(form.customMessage, t('login-limit-message-placeholder'))}
                maxLength={500}
                rows={4}
                placeholder={t('login-limit-message-placeholder')}
                value={form.customMessage}
                onChange={(event) => patch({ customMessage: event.target.value })}
              />
              <p className="form-hint">{t('login-limit-message-help')}</p>
              {(messageProblem(form.customMessage) || errorField === 'customMessage') && (
                <p className="form-error" role="alert">{t('login-limit-message-invalid')}</p>
              )}
            </div>

            <div className="form-group">
              <label className="settings-page__toggle">
                <input
                  type="checkbox"
                  checked={form.type === 'named' || form.askForLoginId || blockLocksAsk}
                  disabled={form.type === 'named' || blockLocksAsk}
                  onChange={(event) => {
                    if (form.type === 'named' || blockLocksAsk) return;
                    patch({ askForLoginId: event.target.checked });
                  }}
                />
                <span>{t('login-limit-ask-label')}</span>
              </label>
              <p className="form-hint">
                {t(form.type === 'named'
                  ? 'login-limit-ask-named'
                  : blockLocksAsk
                    ? 'login-limit-license-types-ask-locked'
                    : 'login-limit-ask-help')}
              </p>
              {pageAsks && (
                <div className="login-limit__login-id">
                  <label className="form-label" htmlFor="ll-login-id">{t('login-limit-login-id-label')}</label>
                  <input
                    id="ll-login-id"
                    className="form-input"
                    dir={fieldDir(form.loginIdLabel, t('login-limit-login-id-placeholder'))}
                    maxLength={LOGIN_ID_LABEL_MAX}
                    placeholder={t('login-limit-login-id-placeholder')}
                    value={form.loginIdLabel}
                    onChange={(event) => patch({ loginIdLabel: clipLoginIdLabel(event.target.value) })}
                  />
                  <p className="form-hint">{t('login-limit-login-id-help')}</p>
                  {labelProblem(form.loginIdLabel) && <p className="form-error" role="alert">{t('login-limit-login-id-invalid')}</p>}
                </div>
              )}
              {form.type !== 'named' && zoneShown && (
                <ReportTimeZoneField
                  value={form.reportTimeZone}
                  helpKey="login-limit-time-zone-help-concurrent"
                  onChange={(reportTimeZone) => patch({ reportTimeZone })}
                />
              )}
            </div>

            {form.type === 'named' && (
              <div className="form-group">
                <label className="settings-page__toggle">
                  <input
                    type="checkbox"
                    checked={form.exceptionsReport}
                    onChange={(event) => patch({
                      exceptionsReport: event.target.checked,
                      reportTimeZone: event.target.checked && !form.reportTimeZone ? browserTimeZone() : form.reportTimeZone,
                    })}
                  />
                  <span>{t('login-limit-exceptions-label')}</span>
                </label>
                <p className="form-hint">{t('login-limit-exceptions-help')}</p>
                <p className="form-hint">{t('login-limit-exceptions-privacy')}</p>
                {form.exceptionsReport && (
                  <ReportTimeZoneField
                    value={form.reportTimeZone}
                    helpKey="login-limit-time-zone-help"
                    onChange={(reportTimeZone) => patch({ reportTimeZone })}
                  />
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="ll-allowlist">{t('login-limit-allowlist-label')}</label>
              <textarea
                id="ll-allowlist"
                className="form-input form-textarea"
                dir="ltr"
                rows={4}
                spellCheck={false}
                placeholder={t('login-limit-allowlist-placeholder')}
                value={form.allowlist}
                onChange={(event) => patch({ allowlist: event.target.value })}
              />
              <p className="form-hint">{t('login-limit-allowlist-help')}</p>
              {errorField === 'allowlist' && <p className="form-error">{t('login-limit-allowlist-invalid')}</p>}
            </div>

            <div className="form-group">
              <label className="settings-page__toggle">
                <input
                  type="checkbox"
                  checked={form.alertEnabled}
                  onChange={(event) => patch({ alertEnabled: event.target.checked })}
                />
                <span>{t('login-limit-alert-enable')}</span>
              </label>
              <label className="form-label" htmlFor="ll-alert-email">{t('login-limit-alert-label')}</label>
              <input
                id="ll-alert-email"
                className="form-input"
                type="email"
                dir="ltr"
                autoComplete="email"
                placeholder={threatEmail || undefined}
                value={form.alertEmail}
                onChange={(event) => patch({ alertEmail: event.target.value })}
              />
              <p className="form-hint">
                {threatEmail
                  ? t('login-limit-alert-help', { email: threatEmail })
                  : t(threatMailbox ? 'login-limit-alert-help-none' : 'login-limit-alert-help-standalone')}
              </p>
              {errorField === 'alertEmail' && <p className="form-error">{t('login-limit-alert-invalid')}</p>}
            </div>

            {block && block !== admitUrlKey && !saveError && !needsConnect && !(typeMissing || limitMissing || startMissing || lengthMissing) && (
              <p className="form-hint">{t(block)}</p>
            )}

            <SaveRow
              className="settings-section__save"
              error={admitUrlServerError ? null : saveError}
              message={saveConfirmation.message}
            >
              <SaveButton
                saving={busy === 'save'}
                justSaved={saveConfirmation.justSaved}
                label={t('settings-save')}
                savingLabel={t('settings-saving')}
                onClick={() => void onSave()}
                disabled={block !== null || busy !== null}
              />
            </SaveRow>
          </fieldset>
        </>
      )}
    </section>
  );
}

function ReportTimeZoneField({
  value,
  helpKey,
  onChange,
}: {
  value: string;
  helpKey: string;
  onChange: (zone: string) => void;
}) {
  // UTC leads the list: one neutral day boundary for an organization working in several countries.
  const zones = (value && !REPORT_ZONES.includes(value) ? [value, ...REPORT_ZONES] : REPORT_ZONES)
    .filter((zone) => zone !== 'UTC');
  return (
    <div className="login-limit__zone">
      <label className="form-label" htmlFor="ll-report-zone">{t('login-limit-time-zone-label')}</label>
      {REPORT_ZONES.length > 0 ? (
        <select
          id="ll-report-zone"
          className="form-select"
          dir="ltr"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {!timeZoneOk(value) && (
            <option value="">{t('login-limit-time-zone-required')}</option>
          )}
          <option value="UTC">{t('login-limit-time-zone-utc')}</option>
          {zones.map((zone) => (
            <option key={zone} value={zone}>{zone}</option>
          ))}
        </select>
      ) : (
        <input
          id="ll-report-zone"
          className="form-input"
          dir="ltr"
          value={value}
          onChange={(event) => onChange(event.target.value.trim())}
        />
      )}
      <p className="form-hint">{t(helpKey)}</p>
      {!timeZoneOk(value) && (
        <p className="form-error" role="alert">{t('login-limit-time-zone-required')}</p>
      )}
    </div>
  );
}

function LicenseTypeLimits({
  rows,
  options,
  named,
  retrying,
  onRetry,
  onLimit,
  onType,
  onAction,
  onRemove,
  onAdd,
}: {
  rows: LicenseTypeRow[];
  options: LicenseTypeOptions;
  named: boolean;
  retrying: boolean;
  onRetry: () => void;
  onLimit: (index: number, limit: string) => void;
  onType: (index: number, id: string) => void;
  onAction: (index: number, action: LicenseTypeLimitAction) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  const choices = options.options.map((option) => option.id);
  const unused = choices.filter((id) => !rows.some((row) => row.id === id));
  const invalid = licenseTypesProblem(rows);
  const blocking = rows.some((row) => row.action === 'block');
  // Add is never disabled without one of these reasons on screen.
  const addReason = options.state === 'not_connected'
    ? 'login-limit-license-types-not-connected'
    : options.state === 'unreadable'
      ? 'login-limit-license-types-unread'
      : rows.length >= MAX_LICENSE_TYPE_ROWS
        ? 'login-limit-license-types-max'
        : choices.length === 0
          ? 'login-limit-license-types-none'
          : unused.length === 0
            ? 'login-limit-license-types-all-added'
            : null;
  return (
    <div className="form-group">
      <p className="form-label">{t('login-limit-license-types-label')}</p>
      <p className="form-hint">{t(named ? 'login-limit-license-types-help-named' : 'login-limit-license-types-help')}</p>
      {rows.length === 0 && addReason === null && (
        <p className="form-hint">{t('login-limit-license-types-empty')}</p>
      )}
      {rows.length > 0 && (
        <table className="login-limit__types">
          <thead>
            <tr>
              <th scope="col">{t('login-limit-license-types-col-type')}</th>
              <th scope="col">{t('login-limit-license-types-col-limit')}</th>
              <th scope="col">{t('login-limit-license-types-col-action')}</th>
              <th scope="col"><span className="sr-only">{t('login-limit-license-types-remove')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const withdrawn = licenseTypeWithdrawn(row.id, options);
              const typeChoices = [
                row.id,
                ...choices.filter((id) => id !== row.id && !rows.some((other) => other.id === id)),
              ];
              const limitInvalid = limitNumber(row.limit) === null;
              const noteId = `ll-type-limit-note-${index}`;
              return (
                <tr key={`${row.id}-${index}`}>
                  <td>
                    <label className="sr-only" htmlFor={`ll-type-limit-${index}`}>{t('login-limit-license-types-col-type')}</label>
                    <select
                      id={`ll-type-limit-${index}`}
                      className="form-select"
                      value={row.id}
                      disabled={withdrawn || typeChoices.length < 2}
                      aria-describedby={withdrawn ? noteId : undefined}
                      onChange={(event) => onType(index, event.target.value)}
                    >
                      {typeChoices.map((id) => (
                        <option key={id} value={id}>{licenseTypeLabel(id, options)}</option>
                      ))}
                    </select>
                    {withdrawn && (
                      <p id={noteId} className="form-hint">
                        {t(isRetiredLicenseTypeId(row.id) ? 'login-limit-license-types-retired' : 'login-limit-license-types-not-offered')}
                      </p>
                    )}
                  </td>
                  <td>
                    <label className="sr-only" htmlFor={`ll-type-limit-n-${index}`}>{t('login-limit-license-types-col-limit')}</label>
                    <NumberStepper
                      id={`ll-type-limit-n-${index}`}
                      invalid={limitInvalid}
                      describedBy={withdrawn ? noteId : undefined}
                      disabled={withdrawn}
                      value={row.limit}
                      decreaseLabel={t('login-limit-type-limit-decrease', { type: licenseTypeLabel(row.id, options) })}
                      increaseLabel={t('login-limit-type-limit-increase', { type: licenseTypeLabel(row.id, options) })}
                      onChange={(value) => onLimit(index, value)}
                    />
                  </td>
                  <td>
                    <label className="sr-only" htmlFor={`ll-type-limit-action-${index}`}>{t('login-limit-license-types-col-action')}</label>
                    <select
                      id={`ll-type-limit-action-${index}`}
                      className="form-select"
                      value={row.action}
                      aria-describedby={withdrawn ? noteId : undefined}
                      disabled={withdrawn}
                      onChange={(event) => onAction(index, event.target.value === 'block' ? 'block' : 'alert')}
                    >
                      <option value="alert">{t('login-limit-license-types-alert')}</option>
                      <option value="block">{t('login-limit-license-types-block')}</option>
                    </select>
                  </td>
                  <td>
                    <button type="button" className="btn btn--secondary" onClick={() => onRemove(index)}>
                      {t('login-limit-license-types-remove')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {blocking && <p className="form-hint">{t('login-limit-license-types-block-help')}</p>}
      {invalid && <p className="form-error" role="alert">{t('login-limit-license-types-invalid')}</p>}
      {addReason && (
        <p id="ll-type-limit-add-reason" className="form-hint" role="status">
          {t(addReason, { max: String(MAX_LICENSE_TYPE_ROWS) })}
        </p>
      )}
      <div className="login-limit__actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onAdd}
          disabled={addReason !== null}
          aria-describedby={addReason ? 'll-type-limit-add-reason' : undefined}
        >
          {t('login-limit-license-types-add')}
        </button>
        {options.state === 'unreadable' && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onRetry}
            disabled={retrying}
            aria-busy={retrying}
          >
            {retrying ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                {t('login-limit-license-types-retrying')}
              </>
            ) : t('login-limit-license-types-retry')}
          </button>
        )}
      </div>
    </div>
  );
}
